import { compute3DCartesianPositionFromGPSLocation, convertEUSTupleToENUTuple } from "@skydio/math";
import { FrameType_Enum } from "@skydio/pbtypes/pbtypes/vehicle/skills/waypoints";

import { getElevationProbe } from "./getElevationProbe";

import type { GPSPoint } from "@skydio/math";
import type { GotoWaypointActionArgs } from "@skydio/pbtypes/pbtypes/vehicle/skills/actions/goto_waypoint/goto_waypoint";
import type { Action, Mission } from "@skydio/pbtypes/pbtypes/vehicle/skills/mission";

function isActionType(action: Action, type: string): boolean {
  return action.actionKey.toLowerCase() === type.toLowerCase();
}

/**
 * A filter to determine if a mission is fly to POI.
 * A POI mission has a single sequence of gotoWaypoint, initializeGps or timedWait actions,
 * instead of a sequence of sequences of Waypoint types (see isWaypoint function)
 */
export const poiMissionFilter = (mission: Mission) => {
  const missionSequenceActions = mission.actions?.[0]?.args?.sequence?.actions;
  return missionSequenceActions?.every(
    action =>
      isActionType(action, "gotoWaypoint") ||
      isActionType(action, "initializeGps") ||
      isActionType(action, "timedWait")
  );
};

// Distance between two lat/lng points in meters, uses haversine formula which should work well
// over small distances
// http://www.movable-type.co.uk/scripts/latlong.html
// https://en.wikipedia.org/wiki/Haversine_formula
export const distanceFromLatLng = (
  [lat1, lng1]: [number, number],
  [lat2, lng2]: [number, number]
) => {
  const EARTH_RADIUS = 6371e3; // meters
  const dLat = degToRad(lat2 - lat1);
  const dLng = degToRad(lng2 - lng1);
  const halfChordLength =
    Math.pow(Math.sin(dLat / 2), 2) +
    Math.cos(degToRad(lat1)) * Math.cos(degToRad(lat2)) * Math.pow(Math.sin(dLng / 2), 2);
  const angularDistance =
    2 * Math.atan2(Math.sqrt(halfChordLength), Math.sqrt(1 - halfChordLength));
  return EARTH_RADIUS * angularDistance;
};

function degToRad(deg: number): number {
  return (deg * Math.PI) / 180;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TrackAnalyticFn = (event: string, data: any) => void;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
let _trackAnalyticFn: TrackAnalyticFn = (_event, _data) => {};

/**
 * Configure the analytics callback used by the simulation.
 * By default this is a no-op. Consumers (e.g. customer portal) should call
 * this with their real analytics function so simulation telemetry is captured.
 */
export function setTrackAnalytic(fn: TrackAnalyticFn): void {
  _trackAnalyticFn = fn;
}

/**
 * Track an analytics event. Delegates to the callback set via setTrackAnalytic,
 * which defaults to a no-op.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function trackAnalytic(event: string, data: any): void {
  _trackAnalyticFn(event, data);
}

/**
 * Extracts waypoint XY coordinates as geodetic coordinates based on frame type.
 * Currently, returns the coordinates only if they are in GPS frame, otherwise returns undefined.
 */
export function getWaypointXYGeodetic(
  waypoint: GotoWaypointActionArgs["waypoint"]
): { lat: number; lon: number } | undefined {
  if (waypoint?.xy == null) return undefined;

  switch (waypoint.xy.frame) {
    case FrameType_Enum.GPS:
      if (waypoint.xy.x != null && waypoint.xy.y != null) {
        return { lat: waypoint.xy.x, lon: waypoint.xy.y };
      }
      return undefined;
    default:
      // For all other frames (NAV, NAV_TAKEOFF, WORLD_TAKEOFF, etc.),
      // return undefined to avoid rendering at null island
      // Note(Nacho): we could enhance this in the future by either passing the NAV origin to this
      // function (although unclear if we can get it in RFD) or by directly defining the mission
      // origin to the NAV origin and returning here the NAV xy coords
      return undefined;
  }
}

// Using the existing GPSPoint type for geodetic coordinates

// Type for cartesian coordinates [x, y, z]
type CartesianCoordinates = [number, number, number];

// Estimates for each action execution
export type ActionEstimate = {
  batteryUsage: number;
  duration: number;
  photosTaken: number;
  /**
   * Index of the goto waypoint in the mission
   */
  gotoIndex?: number;
  /**
   * Geodetic trajectory of the drone during the action, altitudes are in EGM96(MSL) unless otherwise stated
   */
  trajectory: GPSPoint[];
  /**
   * Cartesian trajectory of the drone during the action, relative to the world origin in drone state
   */
  cartesianTrajectory: CartesianCoordinates[];
  /**
   * Whether the action uses pathfinder
   */
  usePathfinder: boolean;
};

// Action estimate with action ID
export type ActionEstimateWithId = {
  actionUuid: Action["actionUuid"];
  actionKey: Action["actionKey"];
  estimate: ActionEstimate;
};

// Current state of the drone during simulation
type DroneState = {
  battery: number;
  /**
   * Geodetic position of the drone, altitude is in EGM96(MSL)
   */
  position?: GPSPoint;
  cartesianPosition?: CartesianCoordinates;
  flightTime: number;
  photosTaken: number;
  gotoIndex?: number;
  /**
   * Geodetic trajectory of the drone during the mission, altitudes are in EGM96(MSL) unless otherwise stated
   */
  trajectory: GPSPoint[];
  /**
   * Cartesian trajectory of the drone during the mission, relative to the world origin
   */
  cartesianTrajectory: CartesianCoordinates[];
  /**
   * World origin, altitude is in EGM96(MSL)
   */
  worldOrigin: GPSPoint; // NOTE(Nacho): this should be close to the world frame origin
};

// A single object to hold all parameters for a simulation step.
type SimulatorParams = {
  /** The action to simulate */
  action: Action;
  /** The current state of the drone */
  state: DroneState;
  /** The current estimates for the mission */
  estimates: ActionEstimateWithId[];
  /** The origin point for the conversion */
  origin: GPSPoint;
};

// Type for a function that simulates a single action.
type ActionSimulatorResult = { estimates: ActionEstimateWithId[]; state: DroneState };
type ActionSimulator = (
  params: SimulatorParams
) => ActionSimulatorResult | Promise<ActionSimulatorResult>;

// Result of the mission simulation
export type SimulationResult = {
  finalState: DroneState;
  actionEstimates: ActionEstimateWithId[];
  success: boolean;
  failureReason?: string;
  origin: GPSPoint;
  /**
   * The mode in which the vehicle executes the mission
   * - "static" mode means the mission can't be edited while it's being executed. The trajectory is displayed with the passed and planned waypoints, allowing to track the drone's progress. The vehicle might not be on the trajectory track.
   * - "dynamic" mode means the mission can be edited while it's being executed. The trajectory is displayed from the drone's current position, without the portion of the trajectory that has already been completed.
   */
  executionMode: "static" | "dynamic" | "unknown";
};

// This was taken from cloud/clients/customer_portal/src/app/(new)/o/[orgId]/(feature-flagged)/(real-time-ops)/_components/map/missions/MissionPreviewTrajectory.tsx
const METERS_PER_SECOND = 14.0;
const METERS_PER_SECOND_CLIMB = 6.0;
const METERS_PER_SECOND_DESCENT = 4.0;
const WASTED_TIME_SECONDS = 4.0;
const MAX_FLIGHT_TIME_SECONDS = 40 * 60;

/**
 * Creates a GPSPoint object
 * @param latitude Latitude in degrees
 * @param longitude Longitude in degrees
 * @param altitude Altitude in meters
 * @returns GPSPoint object
 */
function createGPSPoint(latitude: number, longitude: number, altitude: number): GPSPoint {
  return {
    lat: latitude,
    lng: longitude,
    alt: altitude,
  };
}

/**
 * Converts geodetic coordinates to cartesian coordinates
 * @param geodetic GPSPoint with lat, lng, alt
 * @param origin Origin point for the conversion
 * @returns Cartesian coordinates [x, y, z]
 */
function convertToCartesian(geodetic: GPSPoint, origin: GPSPoint): CartesianCoordinates {
  const cartesian = convertEUSTupleToENUTuple(
    compute3DCartesianPositionFromGPSLocation({
      gpsLocation: [geodetic.lng, geodetic.lat, geodetic.alt],
      sceneOrigin: [origin.lng, origin.lat, origin.alt],
    })
  ) as CartesianCoordinates;

  return cartesian;
}

/**
 * Updates the drone state based on the current action estimate
 * @param params The simulator parameters
 * @returns The updated drone state
 */
function updateDroneState({
  state,
  estimate,
  origin,
}: {
  /** The current state of the drone */
  state: DroneState;
  /** The action estimate with ID */
  estimate: ActionEstimate;
  /** The origin point for the conversion */
  origin: GPSPoint;
}): DroneState {
  // Start with a copy of the current state
  const newState = { ...state };

  // Apply the effects of the current action estimate
  newState.battery -= estimate.batteryUsage;
  newState.flightTime += estimate.duration;
  newState.photosTaken += estimate.photosTaken;
  newState.gotoIndex = estimate.gotoIndex; // gotoIndex is already incremented by 1 in the estimate

  // Update position to the last point in the trajectory if available
  if (estimate.trajectory.length > 0) {
    const lastPoint = estimate.trajectory[estimate.trajectory.length - 1];
    newState.position = lastPoint;
    newState.cartesianPosition = convertToCartesian(lastPoint, origin);
  }

  // Add trajectory points to the overall trajectory
  newState.trajectory = [...newState.trajectory, ...estimate.trajectory];

  // Add cartesian trajectory points
  const newCartesianPoints = estimate.cartesianTrajectory;
  newState.cartesianTrajectory = [...newState.cartesianTrajectory, ...newCartesianPoints];

  return newState;
}

async function getWaypointAltitude(
  waypoint: GotoWaypointActionArgs["waypoint"],
  origin: GPSPoint
): Promise<number | undefined> {
  if (waypoint?.z == null) return undefined;
  switch (waypoint.z.frame) {
    case FrameType_Enum.GPS:
      return waypoint.z.value;
    case FrameType_Enum.NAV:
    case FrameType_Enum.NAV_TAKEOFF:
    case FrameType_Enum.WORLD_TAKEOFF:
      if (origin.alt == null) {
        console.warn("Origin altitude is undefined");
      }
      return waypoint.z.value + (origin.alt || 0);
    case FrameType_Enum.GPS_ABOVE_GROUND_LEVEL: {
      const waypointGeodetic = getWaypointXYGeodetic(waypoint);
      if (!waypointGeodetic) {
        return waypoint.z.value + (origin.alt || 0);
      }
      const terrainElevation = await getElevationProbe().getElevation(
        waypointGeodetic.lon,
        waypointGeodetic.lat
      );
      return waypoint.z.value + terrainElevation;
    }
    default:
      return undefined;
  }
}

/**
 * Simulates the execution of a gotoWaypoint action
 * @param params The simulator parameters
 * @returns Updated array of action estimates with this action's estimate added
 */
async function simulateGotoWaypointAction({
  action,
  state,
  estimates,
  origin,
}: SimulatorParams): Promise<ActionSimulatorResult> {
  const waypoint = action.args?.gotoWaypoint?.waypoint;
  const waypointGeodetic = getWaypointXYGeodetic(waypoint);
  const waypointAlt = await getWaypointAltitude(waypoint, origin);
  const finalPosition: GPSPoint | undefined =
    waypointGeodetic && waypointAlt
      ? createGPSPoint(waypointGeodetic.lat, waypointGeodetic.lon, waypointAlt)
      : undefined;

  // Create geodetic trajectory
  const geodeticTrajectory: GPSPoint[] = [];
  if (state.position) {
    geodeticTrajectory.push(state.position);
  }
  if (finalPosition) {
    geodeticTrajectory.push(finalPosition);
  }

  // Convert to cartesian coordinates
  const cartesianTrajectory = geodeticTrajectory.map(point => convertToCartesian(point, origin));

  // Get distance, battery and duration estimates
  let duration = 0;
  let batteryUsage = 0;
  if (geodeticTrajectory.length > 1) {
    const from: [number, number] = [geodeticTrajectory[0].lat, geodeticTrajectory[0].lng];
    const to: [number, number] = [geodeticTrajectory[1].lat, geodeticTrajectory[1].lng];
    const horizontalDistance = distanceFromLatLng(from, to);
    const verticalDistance =
      geodeticTrajectory[1].alt && geodeticTrajectory[0].alt
        ? geodeticTrajectory[1].alt - geodeticTrajectory[0].alt
        : 0;
    const climbDistance = Math.max(0, verticalDistance);
    const descentDistance = Math.max(0, -verticalDistance);
    duration =
      WASTED_TIME_SECONDS +
      horizontalDistance / METERS_PER_SECOND +
      climbDistance / METERS_PER_SECOND_CLIMB +
      descentDistance / METERS_PER_SECOND_DESCENT;

    // Note(Nacho): very simplistic, assumes linear usage of battery with time, with a max flight time of 40 mins
    batteryUsage = (duration * 100) / MAX_FLIGHT_TIME_SECONDS;
  }

  const estimate: ActionEstimate = {
    batteryUsage,
    duration,
    photosTaken: 0,
    trajectory: geodeticTrajectory,
    cartesianTrajectory,
    gotoIndex: (state.gotoIndex ?? -1) + 1,
    usePathfinder: action.args?.gotoWaypoint?.motionArgs?.traversalArgs?.usePathfinder ?? false,
  };

  return {
    estimates: [
      ...estimates,
      { actionUuid: action.actionUuid, actionKey: action.actionKey, estimate },
    ],
    state: updateDroneState({ state, estimate, origin }),
  };
}

/**
 * Simulates the execution of a takePhoto action
 * @param params The simulator parameters
 * @returns Updated array of action estimates with this action's estimate added
 */
function simulateTakePhotoAction({
  action,
  estimates,
  state,
  origin,
}: SimulatorParams): ActionSimulatorResult {
  // Mock implementation for now
  const estimate: ActionEstimate = {
    batteryUsage: 0.2, // 0.2% battery usage
    duration: 2, // 2 seconds
    photosTaken: 1, // 1 photo taken
    trajectory: [],
    cartesianTrajectory: [],
    gotoIndex: state.gotoIndex,
    usePathfinder: false,
  };

  return {
    estimates: [
      ...estimates,
      { actionUuid: action.actionUuid, actionKey: action.actionKey, estimate },
    ],
    state: updateDroneState({ state, estimate, origin }),
  };
}

/**
 * Simulates the execution of a timedWait action
 * @param params The simulator parameters
 * @returns Updated array of action estimates with this action's estimate added
 */
function simulateTimedWaitAction({
  action,
  estimates,
  state,
  origin,
}: SimulatorParams): ActionSimulatorResult {
  // Get wait duration from action or default to 5 seconds
  const waitDuration = action.args?.timedWait?.timeout?.value || 5;

  // Mock implementation for now
  const estimate: ActionEstimate = {
    batteryUsage: waitDuration * 0.05, // 0.05% battery usage per second
    duration: waitDuration,
    photosTaken: 0, // No photos taken during wait
    trajectory: [],
    cartesianTrajectory: [],
    gotoIndex: state.gotoIndex,
    usePathfinder: false,
  };

  return {
    estimates: [
      ...estimates,
      { actionUuid: action.actionUuid, actionKey: action.actionKey, estimate },
    ],
    state: updateDroneState({ state, estimate, origin }),
  };
}

/**
 * Simulates the execution of a setObstacleAvoidance action
 * @param params The simulator parameters
 * @returns Updated array of action estimates with this action's estimate added
 */
function simulateSetObstacleAvoidanceAction({
  action,
  estimates,
  state,
  origin,
}: SimulatorParams): ActionSimulatorResult {
  // Mock implementation for now - this action is instantaneous and doesn't affect estimates directly
  const estimate: ActionEstimate = {
    batteryUsage: 0,
    duration: 0,
    photosTaken: 0,
    trajectory: [],
    cartesianTrajectory: [],
    gotoIndex: state.gotoIndex,
    usePathfinder: false,
  };

  return {
    estimates: [
      ...estimates,
      { actionUuid: action.actionUuid, actionKey: action.actionKey, estimate },
    ],
    state: updateDroneState({ state, estimate, origin }),
  };
}

/**
 * Simulates the execution of a togglePhotoIntervalMode action
 * @param params The simulator parameters
 * @returns Updated array of action estimates with this action's estimate added
 */
function simulateTogglePhotoIntervalModeAction({
  action,
  estimates,
  state,
  origin,
}: SimulatorParams): ActionSimulatorResult {
  // Mock implementation for now - this action is instantaneous and doesn't affect estimates directly
  const estimate: ActionEstimate = {
    batteryUsage: 0.1, // 0.1% battery usage
    duration: 1, // 1 second
    photosTaken: 0, // No photos taken during toggle (photos will be taken later)
    trajectory: [],
    cartesianTrajectory: [],
    gotoIndex: state.gotoIndex,
    usePathfinder: false,
  };

  return {
    estimates: [
      ...estimates,
      { actionUuid: action.actionUuid, actionKey: action.actionKey, estimate },
    ],
    state: updateDroneState({ state, estimate, origin }),
  };
}

/**
 * Simulates the execution of a startVideo or stopVideo action
 * @param params The simulator parameters
 * @returns Updated array of action estimates with this action's estimate added
 */
function simulateVideoAction({
  action,
  estimates,
  state,
  origin,
}: SimulatorParams): ActionSimulatorResult {
  // Mock implementation for now - this action is instantaneous and doesn't affect estimates directly
  const estimate: ActionEstimate = {
    batteryUsage: 0.1, // 0.1% battery usage
    duration: 1, // 1 second
    photosTaken: 0, // No photos taken during video actions
    trajectory: [],
    cartesianTrajectory: [],
    gotoIndex: state.gotoIndex,
    usePathfinder: false,
  };

  return {
    estimates: [
      ...estimates,
      { actionUuid: action.actionUuid, actionKey: action.actionKey, estimate },
    ],
    state: updateDroneState({ state, estimate, origin }),
  };
}

/**
 * Simulates the execution of a sequence action by recursively simulating its child actions
 * @param params The simulator parameters
 * @returns Updated array of action estimates with all child actions' estimates added
 */
async function simulateSequenceAction({
  action,
  state,
  estimates,
  origin,
}: SimulatorParams): Promise<ActionSimulatorResult> {
  // Get child actions
  const childActions = action.args?.sequence?.actions || [];

  // Initialize with current estimates
  const actionResult: ActionSimulatorResult = { estimates: [...estimates], state };

  // Recursively simulate each child action
  for (const childAction of childActions) {
    const { estimates, state: newState } = await simulateAction({
      action: childAction,
      state: actionResult.state,
      estimates: actionResult.estimates,
      origin,
    });
    actionResult.estimates = estimates;
    actionResult.state = newState;
  }

  return actionResult;
}

const ACTION_SIMULATORS: Record<string, ActionSimulator> = {
  sequence: simulateSequenceAction,
  gotowaypoint: simulateGotoWaypointAction,
  takephoto: simulateTakePhotoAction,
  timedwait: simulateTimedWaitAction,
  setobstacleavoidance: simulateSetObstacleAvoidanceAction,
  togglephotointervalmode: simulateTogglePhotoIntervalModeAction,
  startvideo: simulateVideoAction,
  stopvideo: simulateVideoAction,
} as const;

/**
 * Simulates the execution of any action by dispatching to the appropriate simulator
 * @param params The simulator parameters
 * @returns Updated array of action estimates with this action's estimate added
 */
export async function simulateAction(params: SimulatorParams): Promise<ActionSimulatorResult> {
  const { action, estimates } = params;
  // Normalize key to lowercase to match map entries
  const key = action.actionKey.toLowerCase();
  const simulator = ACTION_SIMULATORS[key];

  if (simulator) {
    return await simulator(params);
  }

  // For unsupported action types, create a minimal estimate and return
  console.warn(`Unsupported action type: ${action.actionKey}`);
  trackAnalytic("simLite.simulateAction.unsupportedActionType", { action });

  return {
    state: params.state,
    estimates: [
      ...estimates,
      {
        actionUuid: action.actionUuid,
        actionKey: action.actionKey,
        estimate: {
          batteryUsage: 0,
          duration: 0,
          photosTaken: 0,
          trajectory: [],
          cartesianTrajectory: [],
          gotoIndex: params.state.gotoIndex,
          usePathfinder: false,
        },
      },
    ],
  };
}

/**
 * Simulates the execution of a mission
 * @param mission The mission to simulate
 * @param initialState The initial drone state (if not provided, a default state will be used)
 * @returns The simulation result including final state and action estimates
 */
export async function simulateMissionExecution(
  mission: Mission,
  initialState: Pick<DroneState, "worldOrigin"> & Partial<DroneState>
): Promise<SimulationResult> {
  // Create a complete initial state with default values if not provided
  const defaultState: Omit<DroneState, "worldOrigin"> = {
    battery: 100,
    position: undefined,
    cartesianPosition: undefined,
    flightTime: 0,
    photosTaken: 0,
    gotoIndex: undefined,
    trajectory: [],
    cartesianTrajectory: [],
  };

  // Merge provided initialState with defaults
  const completeInitialState: DroneState = {
    ...defaultState,
    ...initialState,
  };

  // Define the GPS origin for coordinate conversion
  // Using a default location (Clearview) if not available
  const origin: GPSPoint = completeInitialState.worldOrigin;

  // Initialize result object
  const missionResult: ActionSimulatorResult = { estimates: [], state: completeInitialState };
  try {
    // Simulate the mission execution
    for (const action of mission.actions) {
      const { estimates, state: newState } = await simulateAction({
        action,
        state: missionResult.state,
        estimates: missionResult.estimates,
        origin,
      });
      missionResult.estimates.push(...estimates);
      missionResult.state = newState;
    }

    // Get the mission mode, currently only fly to point missions are dynamic
    const executionMode = poiMissionFilter(mission) ? "dynamic" : "static";

    return {
      finalState: missionResult.state,
      actionEstimates: missionResult.estimates,
      success: true,
      origin,
      executionMode,
    };
  } catch (error) {
    trackAnalytic("simLite.simulateMissionExecution.simulationError", {
      error,
      input: { mission, initialState },
    });
    return {
      finalState: completeInitialState,
      actionEstimates: [],
      success: false,
      failureReason: error instanceof Error ? error.message : "Unknown error",
      origin,
      executionMode: "unknown",
    };
  }
}
